// src/utils/pipelineView.js
// Pure helpers for the pipeline "blend" view (funnel strip + needs-attention
// band + stage-grouped rows). No React, no Firestore — data in, view-model out.
// Spec: docs/superpowers/specs/2026-07-15-pipeline-blend-redesign-design.md

import { parseLocalDate } from "./statusSheetData";

// A lead needs attention when it's actively being worked but has NO next touch
// on the calendar — no follow-up date and no appointment. Parked leads
// (Deferred / Next Year) are deliberately waiting, so they never count.
export const needsAttentionLead = (lead, { parkingStages = [] } = {}) =>
  String(lead?.status || "active").toLowerCase() === "active" &&
  !parkingStages.includes(lead?.stage || "") &&
  !String(lead?.nextFollowUp || "").trim() &&
  !String(lead?.appointmentAt || "").trim();

// The lead's next touch as a sortable time (appointment wins over follow-up);
// leads with nothing scheduled sort last within their group.
export const nextTouchTime = (lead) => {
  const dt =
    parseLocalDate(lead?.appointmentAt) || parseLocalDate(lead?.nextFollowUp);
  return dt ? dt.getTime() : Infinity;
};

// Build the blend view-model:
//   needs   — active leads with no next touch (the red band; All-stages view only)
//   groups  — [{stage, rows}] for non-parking stages, EXCLUDING the needy rows
//             (they show only in the band — no duplicates), sorted by next touch
//   waiting — leads in parking stages (compact "Waiting" band)
//   counts  — per-stage totals INCLUDING needy/parked (the strip numbers)
export function groupPipelineView(leads, { stages = [], parkingStages = [] } = {}) {
  const list = Array.isArray(leads) ? leads : [];
  const stageOf = (l) => l?.stage || stages[0] || "";
  const counts = {};
  stages.forEach((s) => {
    counts[s] = list.filter((l) => stageOf(l) === s).length;
  });

  const needs = list
    .filter((l) => needsAttentionLead(l, { parkingStages }))
    .sort((a, b) => nextTouchTime(a) - nextTouchTime(b));
  const needsIds = new Set(needs.map((l) => l.id));

  const groups = stages
    .filter((s) => !parkingStages.includes(s))
    .map((stage) => ({
      stage,
      rows: list
        .filter((l) => stageOf(l) === stage && !needsIds.has(l.id))
        .sort((a, b) => nextTouchTime(a) - nextTouchTime(b)),
    }))
    .filter((g) => g.rows.length > 0 || needs.some((l) => stageOf(l) === g.stage));

  const waiting = list
    .filter((l) => parkingStages.includes(stageOf(l)))
    .sort((a, b) => nextTouchTime(a) - nextTouchTime(b));

  return { needs, groups, waiting, counts, total: list.length };
}

// Focused view for one stage: everyone in it, the needy ones first, then by
// next touch date.
export function focusStageRows(leads, stage, { parkingStages = [] } = {}) {
  const list = Array.isArray(leads) ? leads : [];
  return list
    .filter((l) => (l?.stage || "") === stage || (!l?.stage && stage === "New Lead"))
    .sort((a, b) => {
      const an = needsAttentionLead(a, { parkingStages }) ? 0 : 1;
      const bn = needsAttentionLead(b, { parkingStages }) ? 0 : 1;
      return an - bn || nextTouchTime(a) - nextTouchTime(b);
    });
}
